'use client'

import Link from 'next/link'
import { useClock } from '../lib/use-clock'
import { LEGAL_NAME } from '../seo'

export function Footer() {
  const time = useClock()

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-brand">
              <span className="footer-brand-name">Payna</span>
            </div>
          </div>
          <div className="footer-meta">
            <span className="footer-location">
              San Francisco, CA
              {time && ` · ${time} PST`}
            </span>
          </div>
        </div>

        <div className="footer-nav">
          <div className="footer-nav-group">
            <span className="footer-nav-label">Company</span>
            <div className="footer-nav-links">
              {/* About and FAQ were absent here with a comment saying
                  they "return when those pages exist". /about has
                  existed since 2026-08-26 and nothing came back for it;
                  /faq shipped 2026-10-14. Both are now sitewide, and
                  both lead the group on purpose: they are the two pages
                  a first-time visitor and a search engine use to work
                  out what this company is. */}
              <Link href="/about" className="footer-nav-link">About</Link>
              <Link href="/faq" className="footer-nav-link">FAQ</Link>
              <Link href="/docs" className="footer-nav-link">Docs</Link>
              <Link href="/blog" className="footer-nav-link">Blog</Link>
              <Link href="/changelog" className="footer-nav-link">Changelog</Link>
              <Link href="/security" className="footer-nav-link">Security</Link>
              <Link href="/contact" className="footer-nav-link">Contact</Link>
            </div>
          </div>
          <div className="footer-nav-group">
            {/* Free reference tables, and the only route to them from
                the rest of the site now the header "Reference" dropdown
                is gone (owner's call, 2026-09). The interactive API
                reference moved here from the top bar at the same time.
                Labels name the SUBJECT, not the audience: "Catastrophe
                adjusters" read like a group of people rather than a
                licensing table. */}
            <span className="footer-nav-label">Reference</span>
            <div className="footer-nav-links">
              <Link href="/docs/api" className="footer-nav-link">
                API reference
              </Link>
              <Link href="/licensing" className="footer-nav-link">
                State licensing reference
              </Link>
              <Link href="/licensing/mortgage" className="footer-nav-link">
                Mortgage licensing by state
              </Link>
              <Link
                href="/catastrophe-adjuster-licensing"
                className="footer-nav-link"
              >
                Catastrophe adjuster licensing by state
              </Link>
            </div>
          </div>
          <div className="footer-nav-group">
            <span className="footer-nav-label">Legal</span>
            <div className="footer-nav-links">
              <Link href="/terms-of-service" className="footer-nav-link">Terms</Link>
              <Link href="/privacy-policy" className="footer-nav-link">Privacy</Link>
            </div>
          </div>
          <div className="footer-nav-group">
            <span className="footer-nav-label">Connect</span>
            <div className="footer-nav-links">
              <a
                href="https://www.linkedin.com/company/payna"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-nav-link"
              >
                LinkedIn
              </a>
              <a href="mailto:hello@payna.com" className="footer-nav-link">
                Email
              </a>
            </div>
          </div>
          {/* THE ONLY INDUSTRY LINK IN THE FOOTER, AND IT IS ON PURPOSE.

              Until 2026-10-14 the footer linked no vertical at all, so
              every industry page's entire internal link budget came from
              one nav dropdown, one homepage strip and the /about grid,
              identically, seven ways. Nothing in the site's structure
              said which vertical the company actually sells, and
              Google's brand-query sitelinks reflected that back: they
              returned /fintech and /crypto and no /mortgage.

              A sitewide footer link is the one placement that appears on
              every page, so it is the cheapest way to say "this one is
              different" without touching the nav. It stays a list of ONE
              because the moment it becomes seven it says nothing again,
              and because the nav dropdown already carries the full set.
              Keep it in step with whichever vertical leads
              ALL_INDUSTRY_LINKS in app/lib/site.ts.

              LAST, not first. .footer-nav is a 3-column grid above 900px
              whose rows size to their tallest group, so a one-link group
              placed first sat beside Company (seven links) and opened a
              hole six rows deep. Last, it lands in row two beside
              Connect, and the row is only as tall as Connect's two
              links. Footer position carries no ranking weight of its
              own; being on every page is the whole point, and it still
              is. */}
          <div className="footer-nav-group">
            <span className="footer-nav-label">Industry</span>
            <div className="footer-nav-links">
              <Link href="/mortgage" className="footer-nav-link">
                Mortgage licensing
              </Link>
            </div>
          </div>
        </div>

        {/* NON-AFFILIATION, SITE WIDE.

            This sentence already existed, word for word, but only on
            /docs. Meanwhile /seats names NMLS or NIPR ten times,
            /mortgage eight, the homepage seven, and none of them said
            who was speaking. Any page that could rank for a query
            containing "NMLS" has to be unmistakable about that, and
            the registries are trademarks belonging to other bodies.

            It lives in the footer rather than per page so a new page
            cannot be added without it. NIPR is named alongside the
            NMLS bodies because the insurance pages lean on it just as
            heavily. */}
        {/* data-nosnippet: RENDERED AND INDEXED, BUT NOT QUOTABLE.

            Google was using this exact sentence as the snippet under
            three of the five sitelinks on the brand query (observed
            2026-10-14: /fintech, /maintain and /crypto all read "Payna
            is an independent compliance platform. It is not ..."), so
            three different pages described themselves identically and
            told a reader nothing. It is not a missing-description bug:
            every one of those pages serves a good, distinct
            `<meta name="description">`; Google simply preferred the
            flowing prose in the footer, which on these
            heading-and-card pages is some of the only flowing prose
            there is.

            data-nosnippet is the documented way to say "do not quote
            this in a result" without hiding it. The disclaimer still
            renders for every human on every page, is still crawled,
            and still does its non-affiliation job. It just stops
            competing with the page's own description.

            This does NOT let us choose what Google shows instead, only
            what it cannot show. If a page still snippets badly after
            this, the answer is more real prose above the fold on that
            page, not more markup here.

            ⚠ THE WRAPPING div IS LOAD-BEARING. Google supports this
            attribute on span, div and section ONLY, and ignores it on
            every other element. It shipped on the <p> itself first,
            which rendered fine, validated fine, and did exactly
            nothing. Do not "tidy" it back onto the paragraph. */}
        <div data-nosnippet>
          <p className="footer-affiliation">
          Payna is an independent compliance platform. It is not affiliated
          with, endorsed by, or operated by NMLS, the Conference of State
          Bank Supervisors, State Regulatory Registry LLC, NIPR, or the
            NAIC.
          </p>
        </div>

        <div className="footer-bottom">
          <div className="footer-legal">
            <a href="/status" className="footer-legal-link">Status</a>
            <span className="footer-legal-sep">·</span>
            <a href="/terms-of-service" className="footer-legal-link">Terms of Service</a>
            <span className="footer-legal-sep">·</span>
            <a href="/privacy-policy" className="footer-legal-link">Privacy Policy</a>
          </div>
          {/* From the constant, not typed out. This read "Payna
              Technologies, Inc." until 2026-08-25, which is not the
              legal entity and contradicted the legal pages, the
              status page and the JSON-LD legalName on every single
              page of the site. */}
          <p className="footer-rights">
            © 2026 {LEGAL_NAME} All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
